import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Batch } from './batch';
import { isDocumentCompleted } from '../../shared/constants/batch-status';
import { connectorPresentation } from '../../pages/brain/document-sources';

export interface ProcessedDocumentRow {
  id: string;
  batchId: string;
  name: string;
  fileExtension: string;
  dataSource: string;
  sourceIcon: string;
  classification: string;
  subClassification: string;
}

export type ClassifiedSourceId = 'sharepoint' | 'github' | 'outlook';

export interface ClassifiedSource {
  id: ClassifiedSourceId;
  name: string;
  documentCount: number;
  lastUpdated: string;
}

interface CountResponse {
  data: { total: number; items?: unknown[] };
}

@Injectable({ providedIn: 'root' })
export class ClassifiedDocuments {
  private batchService = inject(Batch);

  constructor(private http: HttpClient) {}

  // Persists the list's current pagination page across navigation so returning
  // to the page (e.g. from Document Access) doesn't reset back to page 1.
  currentPage = signal(1);

  getSources(): Observable<ClassifiedSource[]> {
    const spCount$ = this.http
      .get<CountResponse>(`${environment.apiUrl}/connectors/sharepoint/documents/processed`, { params: { page: 1, pageSize: 1 } })
      .pipe(map(r => r.data.total), catchError(() => of(0)));

    const ghCount$ = this.http
      .get<{ data: { totalCount: number } }>(`${environment.apiUrl}/github/repositories?page=1&perPage=1`)
      .pipe(map(r => r.data.totalCount ?? 0), catchError(() => of(0)));

    return forkJoin([spCount$, ghCount$]).pipe(
      map(([spCount, ghCount]) => [
        { id: 'sharepoint' as ClassifiedSourceId, name: 'SharePoint', documentCount: spCount, lastUpdated: new Date().toISOString() },
        { id: 'github' as ClassifiedSourceId, name: 'GitHub', documentCount: ghCount, lastUpdated: new Date().toISOString() },
        { id: 'outlook' as ClassifiedSourceId, name: 'Outlook Mailbox', documentCount: 0, lastUpdated: new Date().toISOString() }
      ])
    );
  }

  getAllProcessedDocuments(): Observable<ProcessedDocumentRow[]> {
    return this.batchService.getBatches(undefined, 1, 200).pipe(
      switchMap(batchesRes => {
        const batches = batchesRes.data.items;
        if (batches.length === 0) return of([]);

        const requests = batches.map(b =>
          this.batchService.getBatchById(b.id, 1, 200).pipe(
            map(res => res.data.batch.documents
              .filter(d => isDocumentCompleted(d.status))
              .map(d => {
                const source = connectorPresentation(
                  d.fileExtension === 'EMAIL' ? 'Mailbox' : (b.sourceProvider || d.connector || b.sourceType)
                );
                return {
                id: d.id,
                batchId: b.id,
                name: d.fileName,
                fileExtension: d.fileExtension,
                dataSource: source.label,
                sourceIcon: source.icon,
                classification: d.classification || 'Unclassified',
                subClassification: d.subClassification || '—'
              };
              })
            ),
            catchError(() => of([]))
          )
        );

        return forkJoin(requests).pipe(map(lists => lists.flat()));
      })
    );
  }
}
